/**
 * SubWorkflowInspector — Phase 6b-5.
 *
 * Invokes another workflow as a sub-process. Source schema:
 * soarNodes.js:2534.
 *
 *   workflowId   UUID         child workflow to invoke
 *   input        object|str   payload passed in (JSON or template)
 *   timeout      number       sub-workflow timeout (seconds, default 600)
 *   onError      enum         fail | continue | use_default
 *
 * Data sources:
 *   - PlatformContext session → orgId/tenantId for the workflow list scope
 *   - api.getWorkflows({orgId, tenantId}) lazy-fetched on mount and cached
 *     in component state for the Inspector lifetime
 *
 * We deliberately do NOT show the current workflow in the picker — a
 * workflow that invokes itself is a runtime stack-overflow waiting to
 * happen; the BE would reject it but better to not even allow the pick.
 *
 * Spread-preservation contract per useCanvas:1098.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePlatform } from '../../../contexts/PlatformContext';
import { getWorkflows } from '../../../services/api';

const ON_ERROR = [
  { value: 'fail',        label: 'Fail the parent workflow' },
  { value: 'continue',    label: 'Continue past the error' },
  { value: 'use_default', label: 'Use default fallback value' },
];

export default function SubWorkflowInspector({ node, onUpdate, readOnly = false }) {
  const cfg = node.config || {};
  const workflowId = cfg.workflowId || '';
  const inputDisplay = stringifyJson(cfg.input);
  const timeout = cfg.timeout ?? '';
  const onError = ON_ERROR.find((o) => o.value === cfg.onError)?.value || 'fail';

  const platform = useSafePlatform();
  const session = platform?.session;
  const orgId = session?.orgId || null;
  const tenantId = session?.tenantId || null;

  // Capture the current workflow id from the URL so we can exclude it
  // from the picker (preventing recursive self-invocation).
  const [searchParams] = useSearchParams();
  const currentWorkflowId = searchParams.get('workflowId') || searchParams.get('id') || null;

  // Lazy workflow list fetch — runs once on mount + re-runs if scope changes.
  const [workflows, setWorkflows] = useState(null);   /* null = loading */
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setWorkflows(null);
    getWorkflows({ orgId, tenantId })
      .then((res) => {
        if (cancelled) return;
        const payload = (res && 'data' in res) ? res.data : res;
        const list = Array.isArray(payload)
          ? payload
          : (Array.isArray(payload?.items) ? payload.items : []);
        setWorkflows(list);
      })
      .catch((e) => {
        if (cancelled) return;
        console.warn('[SubWorkflowInspector] getWorkflows failed', e);
        setError(e?.message || String(e));
        setWorkflows([]);
      });
    return () => { cancelled = true; };
  }, [orgId, tenantId]);

  // Build picker options. Exclude the workflow currently open in the canvas
  // — self-invocation is a footgun even when the BE would reject it. Sort
  // alphabetically for predictable scanning.
  const options = useMemo(() => {
    if (!Array.isArray(workflows)) return [];
    return workflows
      .filter((w) => w && w.id && w.id !== currentWorkflowId)
      .map((w) => ({
        value: w.id,
        label: w.name || w.id,
        status: w.status || '',
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [workflows, currentWorkflowId]);

  const setField = (key, value) => {
    if (readOnly || typeof onUpdate !== 'function') return;
    onUpdate(node.id, { config: { ...cfg, [key]: value } });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <FieldGroup
        label="Workflow"
        hint={workflows === null
          ? 'Loading workflows…'
          : error
            ? `Couldn't load workflows: ${error}`
            : (options.length === 0
              ? 'No other workflows visible. Create one first on the Workflows page.'
              : 'Pick the child workflow to invoke.')}
      >
        <select
          value={workflowId}
          onChange={(e) => setField('workflowId', e.target.value)}
          disabled={readOnly || workflows === null || options.length === 0}
          style={selectStyle}
        >
          <option value="">(none)</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}{o.status && o.status !== 'ACTIVE' ? ` — ${o.status}` : ''}
            </option>
          ))}
          {/* If the persisted workflowId isn't in the freshly fetched list
              (deleted upstream, or different tenant), surface it as a
              legacy option so the saved value doesn't silently drop. */}
          {workflowId && !options.find((o) => o.value === workflowId) && workflows !== null && (
            <option value={workflowId}>{workflowId} (legacy)</option>
          )}
        </select>
      </FieldGroup>

      <FieldGroup
        label="Input Data"
        hint='JSON object passed to the sub-workflow. Supports template strings: ${upstream.field}'
      >
        <textarea
          value={inputDisplay}
          onChange={(e) => setField('input', parseJsonInput(e.target.value))}
          placeholder='{ "alert_id": "${upstream.id}", "severity": "high" }'
          disabled={readOnly}
          rows={5}
          spellCheck={false}
          style={{
            ...inputStyle,
            fontFamily: 'SFMono-Regular, Menlo, monospace',
            fontSize: 11,
            lineHeight: 1.4,
            resize: 'vertical',
          }}
        />
      </FieldGroup>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
        <FieldGroup label="Timeout (s)" hint="Default 600.">
          <input
            type="number"
            min="0"
            step="1"
            value={timeout}
            onChange={(e) => {
              const v = e.target.value;
              if (v === '') return setField('timeout', undefined);
              const n = Number(v);
              if (Number.isFinite(n)) setField('timeout', n);
            }}
            placeholder="600"
            disabled={readOnly}
            style={inputStyle}
          />
        </FieldGroup>

        <FieldGroup label="On Error">
          <select
            value={onError}
            onChange={(e) => setField('onError', e.target.value)}
            disabled={readOnly}
            style={selectStyle}
          >
            {ON_ERROR.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </FieldGroup>
      </div>
    </div>
  );
}

/* ───── helpers ───────────────────────────────────────────────────── */

function useSafePlatform() {
  try { return usePlatform(); } catch (_) { return null; }
}

function stringifyJson(v) {
  if (v === undefined || v === null) return '';
  if (typeof v === 'string') return v;
  try { return JSON.stringify(v, null, 2); } catch (_) { return String(v); }
}

function parseJsonInput(raw) {
  if (typeof raw !== 'string') return raw;
  if (raw.trim() === '') return undefined;
  try { return JSON.parse(raw); } catch (_) { return raw; }
}

/* ───── shared field UI ───────────────────────────────────────────── */

function FieldGroup({ label, hint, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>{label}</span>
      {children}
      {hint && (
        <span style={{ fontSize: 10, color: '#64748b' }}>{hint}</span>
      )}
    </label>
  );
}

const inputStyle = {
  width: '100%',
  padding: '6px 8px',
  fontSize: 12,
  color: '#0f172a',
  background: '#ffffff',
  border: '0.5px solid #cbd5e1',
  borderRadius: 4,
  outline: 'none',
  fontFamily: 'inherit',
};
const selectStyle = { ...inputStyle, appearance: 'auto' };
